/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { ChangeEvent, useCallback, useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask-format';

import { FormControl, FormHelperText, InputAdornment, InputLabel, OutlinedInput, Typography } from '@mui/material';

import { Countries, CountryCode, CountryCodes, Language, Languages } from 'shared';

import { ArrowRoundedIcon } from '../../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../../i18n';
import { useFormStyles } from '../../../../../styles/form';
import { useFormsStyles } from '../PersonalForm.styles';
import { PersonalFormApiValidationErrors, PersonalFormValues, ResetApiErrorForField } from '../PersonalForm.types';

type GeneralFormProps = FormikProps<PersonalFormValues> & {
  apiErrors: PersonalFormApiValidationErrors;
  resetApiError: ResetApiErrorForField;
  isFetching?: boolean;
  errorMessage?: string;
};

export function GeneralForm(props: GeneralFormProps) {
  const {
    touched,
    errors,
    apiErrors,
    values,
    handleChange,
    handleBlur,
    isFetching,
    setFieldValue,
    setFieldTouched,
    resetApiError,
  } = props;
  const classes = useFormsStyles();
  const formClasses = useFormStyles();
  const { t, i18n } = useTranslation(Namespaces.UserProfile);

  const selectedLanguage = useMemo(() => Languages.find(code => code === values.language), [values.language]);

  const hasFirstNameError = touched.firstName && !!(errors.firstName || apiErrors.firstName);
  const hasMiddleNameError = touched.middleName && !!(errors.middleName || apiErrors.middleName);
  const hasLastNameError = touched.lastName && !!(errors.lastName || apiErrors.lastName);
  const hasEmailError = touched.email && !!(errors.email || apiErrors.email);
  const hasLanguageError = touched.language && !!(errors.language || apiErrors.language);
  const hasPhoneCountryCodeError =
    touched.phoneCountryCode && !!(errors.phoneCountryCode || apiErrors.phoneCountryCode);
  const hasPhoneNumberError =
    touched.phoneNationalNumber && !!(errors.phoneNationalNumber || apiErrors.phoneNationalNumber);

  const firstNameError = errors.firstName || apiErrors.firstName;
  const middleNameError = errors.middleName || apiErrors.middleName;
  const lastNameError = errors.lastName || apiErrors.lastName;
  const emailError = errors.email || apiErrors.email;
  const languageError = errors.language || apiErrors.language;
  const phoneCountryCodeError = errors.phoneCountryCode || apiErrors.phoneCountryCode;
  const phoneNumberError = errors.phoneNationalNumber || apiErrors.phoneNationalNumber;

  const onChange = useCallback(
    (name: keyof PersonalFormApiValidationErrors, event: ChangeEvent<HTMLInputElement>) => {
      if (name === 'phoneNationalNumber') {
        event.target.value = event.target.value.replace(/\D/g, '');
      }
      handleChange(event);
      resetApiError(name);
    },
    [handleChange, resetApiError],
  );

  const handleCountryChange = useCallback(
    async (_e: unknown, value: CountryCode | null) => {
      if (value) {
        await setFieldValue('phoneCountryCode', value, true);
        await setFieldTouched('phoneCountryCode', true);
        resetApiError('phoneCountryCode');
      }
    },
    [resetApiError, setFieldTouched, setFieldValue],
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: CountryCode) => (
      <li {...props}>
        <InputAdornment position="start">
          <ReactCountryFlag countryCode={option} svg={true} className={classes.flagIcon} />
        </InputAdornment>
        + {t(`countries:${option}.callingCode`)}
      </li>
    ),
    [classes.flagIcon, t],
  );

  const getOptionLabel = useCallback((option: CountryCode) => `+ ${t(`countries:${option}.callingCode`)}`, [t]);

  const handleChangeLanguage = useCallback(
    async (_e: unknown, code: Language | null) => {
      if (code) {
        await setFieldValue('language', code, true);
        await setFieldTouched('language', true);
        resetApiError('language');
      }
    },
    [resetApiError, setFieldTouched, setFieldValue],
  );

  return (
    <>
      <Typography variant="h4">{t('profile.general_form_title')}</Typography>
      <FormControl className={cx(formClasses.formControl)} error={hasFirstNameError} variant="outlined">
        <InputLabel htmlFor="firstName">{t('profile.label.first_name')}</InputLabel>
        <OutlinedInput
          id="firstName"
          name="firstName"
          label={t('profile.label.first_name')}
          value={values.firstName}
          onChange={onChange.bind(null, 'firstName')}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
        />
        {hasFirstNameError && <FormHelperText>{t(`profile.validation.firstName.${firstNameError}`)}</FormHelperText>}
      </FormControl>
      <FormControl className={cx(formClasses.formControl)} error={hasMiddleNameError} variant="outlined">
        <InputLabel htmlFor="middleName">{t('profile.label.middle_name')}</InputLabel>
        <OutlinedInput
          id="middleName"
          name="middleName"
          value={values.middleName || ''}
          onChange={onChange.bind(null, 'middleName')}
          label={t('profile.label.middle_name')}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
        />
        {hasMiddleNameError && <FormHelperText>{t(`profile.validation.middleName.${middleNameError}`)}</FormHelperText>}
      </FormControl>
      <FormControl className={cx(formClasses.formControl)} error={hasLastNameError} variant="outlined">
        <InputLabel htmlFor="lastName">{t('profile.label.last_name')}</InputLabel>
        <OutlinedInput
          id="lastName"
          name="lastName"
          value={values.lastName}
          onChange={onChange.bind(null, 'lastName')}
          label={t('profile.label.last_name')}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
        />
        {hasLastNameError && <FormHelperText>{t(`profile.validation.lastName.${lastNameError}`)}</FormHelperText>}
      </FormControl>
      <FormControl className={cx(formClasses.formControl)} error={hasEmailError} variant="outlined">
        <InputLabel htmlFor="email">{t('profile.label.email_address')}</InputLabel>
        <OutlinedInput
          id="email"
          name="email"
          value={values.email}
          onChange={onChange.bind(null, 'email')}
          label={t('profile.label.email_address')}
          disabled={isFetching}
          fullWidth
          onBlur={handleBlur}
        />
        {hasEmailError && <FormHelperText>{t(`profile.validation.email.${emailError}`)}</FormHelperText>}
      </FormControl>
      <div className={cx(formClasses.formControl, formClasses.formControlBlock)}>
        <div className={classes.phone}>
          <FormControl variant="outlined">
            <InputLabel htmlFor="phoneCountryCode">{t('profile.label.country_code')}</InputLabel>
            <HUAutocomplete
              popupIcon={<ArrowRoundedIcon />}
              renderInput={renderParams => (
                <OutlinedInput
                  name="phoneCountryCode"
                  label={t('profile.label.country_code')}
                  {...destructRenderInputParams(renderParams)}
                  error={hasPhoneCountryCodeError}
                  startAdornment={
                    <InputAdornment position="start">
                      {values.phoneCountryCode ? (
                        <ReactCountryFlag
                          countryCode={values.phoneCountryCode}
                          svg={true}
                          className={classes.flagIcon}
                        />
                      ) : null}
                    </InputAdornment>
                  }
                />
              )}
              id="phoneCountryCode"
              disabled={isFetching}
              onBlur={() => setFieldTouched('phoneCountryCode', true, true)}
              disableClearable={true}
              options={[...CountryCodes]}
              value={values.phoneCountryCode}
              openOnFocus={true}
              getOptionLabel={getOptionLabel}
              onChange={handleCountryChange}
              renderOption={renderOption}
            />
          </FormControl>
          <FormControl variant="outlined">
            <InputLabel htmlFor="phoneNationalNumber">{t('profile.label.phone_number')}</InputLabel>
            <InputMask
              mask={Countries[values.phoneCountryCode].phone.mask}
              value={values.phoneNationalNumber || ''}
              onChange={onChange.bind(null, 'phoneNationalNumber')}
              disabled={isFetching}
              onBlur={handleBlur}
            >
              <OutlinedInput
                id="phoneNationalNumber"
                disabled={isFetching}
                name="phoneNationalNumber"
                label={t('profile.label.phone_number')}
                error={hasPhoneNumberError}
              />
            </InputMask>
          </FormControl>
        </div>
        {(hasPhoneCountryCodeError || hasPhoneNumberError) && (
          <FormHelperText error>
            {[
              hasPhoneCountryCodeError && t(`profile.validation.phoneCountryCode.${phoneCountryCodeError}`),
              hasPhoneNumberError &&
                t(`profile.validation.phoneNationalNumber.${phoneNumberError}`, {
                  country: Countries[values.phoneCountryCode].i18n[i18n.language as 'en' | 'de'],
                  min: Countries[values.phoneCountryCode].phone.min,
                  max: Countries[values.phoneCountryCode].phone.max,
                }),
            ]
              .filter(v => v && v.length > 0)
              .join('. ')}
          </FormHelperText>
        )}
      </div>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthDefault)}
        error={hasLanguageError}
        variant="outlined"
      >
        <InputLabel htmlFor="language">{t('profile.label.language')}</InputLabel>
        <HUAutocomplete
          popupIcon={<ArrowRoundedIcon />}
          renderInput={renderParams => (
            <OutlinedInput
              {...destructRenderInputParams(renderParams)}
              name="language"
              label={t('profile.label.language')}
            />
          )}
          id="language"
          options={Languages}
          disableClearable={true}
          openOnFocus={true}
          value={selectedLanguage}
          onChange={handleChangeLanguage}
          getOptionLabel={code => t(`profile.language.${code}`)}
        />
        {hasLanguageError && <FormHelperText>{t(`profile.validation.language.${languageError}`)}</FormHelperText>}
      </FormControl>
    </>
  );
}
